import { FunctionComponent, useEffect, useLayoutEffect, useRef, useState } from "react";

import { faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import ReactDOM from "react-dom";

import { Icon } from "@/components/Icon/Icon";
import { classNames } from "@/utils/classNames";
import { useViewportClampedPanel } from "@/utils/useViewportClampedPanel";
import { getComposedEventTarget } from "@/utils/getComposedEventTarget";

interface InstructionHistoryDropdownProps {
	/** Already-filtered, newest-first suggestions to render. */
	suggestions: string[];
	/** Index of the keyboard-highlighted row (-1 = none). */
	highlightedIndex: number;
	/** Element the dropdown anchors to (the instruction input wrapper). */
	anchorRef: React.RefObject<HTMLElement>;
	/**
	 * Portal target. The Chrome extension passes its shadow-root container so the
	 * menu stays inside the styled scope (web rule §11). Defaults to document.body.
	 */
	portalContainer?: HTMLElement | null;
	onSelect: (value: string) => void;
	onClose: () => void;
}

/**
 * Autocomplete list of previously used "Tell AI what to write" instructions.
 * Portals into `portalContainer ?? document.body` with `position: fixed` anchored
 * to the input so it isn't clipped by the GenerateBar card's overflow and renders
 * correctly inside the extension shadow root.
 */
export const InstructionHistoryDropdown: FunctionComponent<InstructionHistoryDropdownProps> = ({
	suggestions,
	highlightedIndex,
	anchorRef,
	portalContainer,
	onSelect,
	onClose,
}) => {
	const dropdownRef = useRef<HTMLDivElement>(null);

	// Keeps the portalled panel inside the viewport wherever the trigger sits (PREC-4138).
	useViewportClampedPanel(dropdownRef, true);
	const [style, setStyle] = useState<React.CSSProperties | null>(null);

	// Anchor to the input's viewport rect; re-run on scroll (capture) + resize so
	// the menu tracks the field. `position: fixed` is required because a shadow-root
	// portal target is not the scroll context (extension).
	useLayoutEffect(() => {
		const updatePosition = () => {
			const anchor = anchorRef.current;

			if (!anchor) {
				return;
			}

			const rect = anchor.getBoundingClientRect();

			setStyle({
				position: "fixed",
				top: rect.bottom + 4,
				left: rect.left,
				width: rect.width,
			});
		};

		updatePosition();

		window.addEventListener("scroll", updatePosition, true);
		window.addEventListener("resize", updatePosition);

		return () => {
			window.removeEventListener("scroll", updatePosition, true);
			window.removeEventListener("resize", updatePosition);
		};
	}, [anchorRef, suggestions.length]);

	// Close on outside click (ignore clicks on the anchored input itself).
	useEffect(() => {
		const handleClick = (event: MouseEvent) => {
			const target = getComposedEventTarget(event);

			const insideAnchor = anchorRef.current?.contains(target as Node);
			const insideDropdown = dropdownRef.current?.contains(target as Node);

			if (!insideAnchor && !insideDropdown) {
				onClose();
			}
		};

		window.addEventListener("mousedown", handleClick);

		return () => window.removeEventListener("mousedown", handleClick);
	}, [anchorRef, onClose]);

	if (suggestions.length === 0) {
		return null;
	}

	return ReactDOM.createPortal(
		<div
			ref={dropdownRef}
			className={classNames(
				"max-h-60 overflow-y-auto bb-scrollbar-darker rounded-xl border border-border-light bg-white shadow-xl",
				"z-[10001]"
			)}
			style={style ?? { visibility: "hidden", position: "fixed" }}
		>
			{suggestions.map((suggestion, index) => (
				<button
					key={suggestion}
					className={classNames(
						"flex w-full items-center gap-2 px-3 py-2 text-left text-ssm text-text",
						index === highlightedIndex ? "bg-background-light" : "hover:bg-background-light"
					)}
					type="button"
					// onMouseDown (not onClick) so the textarea doesn't blur-close the
					// menu before the selection handler runs.
					onMouseDown={(e) => {
						e.preventDefault();
						onSelect(suggestion);
					}}
				>
					<Icon className="shrink-0 text-text-meta" icon={faClockRotateLeft} />
					<span className="truncate">{suggestion}</span>
				</button>
			))}
		</div>,
		portalContainer ?? document.body
	);
};
